import React, { Fragment, useEffect, useMemo, } from 'react';
import { Button, Modal, ModalHeader, ModalBody, ModalFooter, Form, } from 'reactstrap';
import { toast } from 'react-toastify';
import { uniq, keyBy, omit, sortBy, groupBy, pickBy, get, chunk, isEmpty } from 'lodash';
import { format as formatDate, addDays, startOfDay, endOfDay, } from 'date-fns';
import fileDownload from 'js-file-download';
import { TextEncoder, } from 'text-encoding';
import { unparse as unparseCsv, } from 'papaparse';
import numeral from 'numeral';

import firebase, { functions } from '../../firebase';
import { getCollectionData, getDocumentsData, getAllCollectionDataByChunk } from '../../shared/firebase';
import { idWithTenant } from '../../shared/util';
import { cmSizes, kgWeight, } from '../../shared/models/amazonProduct';
import useQueryParams from '../hooks/useQueryParams';
import AdminPage from '../hocs/AdminPage';
import ModelFormModal from '../modals/ModelFormModal';
import useDocumentSubscription from '../hooks/useDocumentSubscription';
import useCollectionSubscription from '../hooks/useCollectionSubscription';
import DateSelector from '../DateSelector';
import ExportButton from '../ExportButton';
import ProgressButton from '../ProgressButton';
import QueryInput from '../QueryInput';

const { entries, keys } = Object;
const db = firebase.firestore();
const settingsRef = db.collection('settings');
const printersRef = db.collection('printers');

export default AdminPage(function AdminExports (props) {
  const { user } = props;
  const {
    startOn: startOnString = formatDate(addDays(new Date(), -30), 'yyyy-MM-dd'),
    endOn: endOnString = formatDate(new Date(), 'yyyy-MM-dd'),
    tenantId: tenantIdFilter = '',
    receivingRequestId: receivingRequestIdFilter = '',
    productId: productIdFilter = '',
  } = useQueryParams();
  const startOn = new Date(startOnString);
  const endOn = new Date(endOnString);
  const inventoryRows = async () => {
    const receivingRequestIdParts = receivingRequestIdFilter.split('-');
    const receivingRequestIdQuery = receivingRequestIdParts.pop();
    const receivingRequestTenantId = receivingRequestIdParts.length > 0 ? receivingRequestIdParts.join('-') : '';
    const productIdParts = productIdFilter.split('-');
    const productIdQuery = productIdParts.pop();
    const productIdTenantId = productIdParts.length > 0 ? productIdParts.join('-') : '';
    const effectiveTenantId = !isEmpty(tenantIdFilter) ? tenantIdFilter : (receivingRequestTenantId || productIdTenantId);
    let ref = db.collection('inventories').where('createdAt', '>=', startOfDay(startOn)).where('createdAt', '<=', endOfDay(endOn)).orderBy('createdAt');
    if (!isEmpty(effectiveTenantId)) {
      ref = ref.where('tenantId', '==', effectiveTenantId);
    }
    if (!isEmpty(receivingRequestIdFilter)) {
      ref = ref.where('receivingRequestId', '==', receivingRequestIdQuery);
    }
    if (!isEmpty(productIdFilter)) {
      ref = ref.where('productId', '==', productIdQuery);
    }
    const inventories = await getAllCollectionDataByChunk(ref);
    const productKeys = uniq(inventories.map(_ => [_.tenantId, _.productId].join('-')));
    const productRefs = productKeys.map((_) => {
      const [tenantId, productId] = _.split('-');
      return db.collection('tenants').doc(tenantId).collection('products').doc(productId);
    });
    const productChunks = chunk(productRefs, 1000);
    const products = (await productChunks.reduce(async (x, refs) => [...await x, ...await getDocumentsData(refs)], Promise.resolve([]))).filter(_ => _.exists);
    const productsById = keyBy(products, _ => idWithTenant(_.ref));
    return inventories.map((inventory) => {
      const {
        id, tenantId, receivingRequestId, receivingRequestDocumentNumber, productId,
        isNg, productBarcodeInfo, locationId, productBarcodeInfoType, receivingRequestInspectType,
        createdAt, status, shelvedAt, pickedAt, shippingRequestId, receivingRequestCheckNote,
        combiningRequestId,
      } = inventory;
      const product = productsById[[tenantId, productId].join('-')];
      return {
        id,
        tenantId,
        receivingRequestId,
        receivingRequestDocumentNumber,
        receivingRequestInspectType,
        receivingRequestCheckNote,
        combiningRequestId,
        productId,
        sku: product?.sku,
        isAmazonProduct: product?.isAmazonProduct,
        isCase: product?.isCase,
        isNg,
        productBarcodeInfo,
        locationId,
        productBarcodeInfoType,
        status,
        locationKey: inventory.locationKey,
        shelvedAt: shelvedAt && formatDate(shelvedAt.toDate(), 'yyyy/MM/dd HH:mm:ss'),
        firstShelvedAt: inventory.firstShelvedAt && formatDate(inventory.firstShelvedAt.toDate(), 'yyyy/MM/dd HH:mm:ss'),
        shippingRequestId,
        createdAt: formatDate(createdAt.toDate(), 'yyyy/MM/dd HH:mm:ss'),
        pickedAt: pickedAt && formatDate(pickedAt.toDate(), 'yyyy/MM/dd HH:mm:ss'),
      };
    });
  };
  const exportProductRows = async () => {
    try {
      await exportProductChunk();
    } catch(e) {
      toast.error(`失敗しました。${e.message}`);
    }
  };
  const exportProductChunk = async (lastDoc) => {
    let ref = db.collectionGroup('products').orderBy('createdAt').limit(10000);
    if(lastDoc) ref = ref.startAfter(lastDoc);
    const products = await getCollectionData(ref, { withDoc: true });
    if(products.length === 0) return;

    const rows = products.map((product) => {
      const { amazonProduct, ref, createdAt } = product;
      const [width, height, length, weight] = [...cmSizes(amazonProduct), kgWeight(amazonProduct)].filter(_ => _ != null).map(_ => numeral(_).format('0.00'));
      return {
        tenantId: ref.parent.parent.id,
        ...omit(product, ['ref', 'exists']),
        width,
        height,
        length,
        weight,
        createdAt: formatDate(createdAt.toDate(), 'yyyy/MM/dd HH:mm:ss'),
      };
    });
    const headers = uniq(rows.flatMap(_ => keys(_)));
    const encoder = new TextEncoder('Shift_JIS', { NONSTANDARD_allowLegacyEncoding: true });
    const fileContent = encoder.encode(unparseCsv({ fields: headers, data: rows.map(_ => headers.map(h => _[h])) }));
    fileDownload(fileContent, '商品.csv');
    const [lastProduct] = products.slice(-1);
    await new Promise(_ => setTimeout(_, 10000));
    await exportProductChunk(lastProduct.doc);
  };

  return (
    <div className="admin-exports container-fluid">
      <div className="p-4 bg-white my-4">
        <div className="d-flex justify-content-center mb-3">
          <h4>データ出力</h4>
        </div>
        <div className="d-flex justify-content-end align-items-end mb-3 gap-1">
          <div className="card p-2">
            <div className="d-flex gap-1 align-items-end">
              <QueryInput paramName="tenantId" label="テナントID" width={150} />
              <QueryInput paramName="receivingRequestId" label="入荷ID" width={200} />
              <QueryInput paramName="productId" label="商品ID" width={150} />
              <DateSelector paramName="startOn" label="作成日(開始)" defaultValue={addDays(new Date(), -30)} />
              <DateSelector paramName="endOn" label="作成日(終了)" invalid={startOn > endOn} defaultValue={new Date()} />
              <ExportButton fileName="inventories.csv" label={`在庫CSV`} rows={inventoryRows} disabled={!(startOnString && endOnString && startOnString <= endOnString)} />
            </div>
          </div>
          <ProgressButton process={exportProductRows}>
            <span className="fas fa-download mr-1" />
            商品CSV
          </ProgressButton>
        </div>
      </div>
    </div>
  );
});
